export type KeywordCategory = "tech" | "soft";

const TECH_LIST: string[] = [
  // ── Python & ecosystem ──────────────────────────────────────────────────────
  "Python",
  // Web frameworks
  "Django", "Django REST Framework", "DRF", "Flask", "FastAPI", "Tornado",
  "Falcon", "Pyramid", "Bottle", "Starlette", "Litestar", "Sanic",
  // Data / ML
  "Pandas", "NumPy", "SciPy", "Matplotlib", "Seaborn", "Plotly", "Bokeh",
  "Scikit-learn", "TensorFlow", "PyTorch", "Keras", "XGBoost", "LightGBM",
  "CatBoost", "Hugging Face", "Transformers", "spaCy", "NLTK", "Gensim",
  "OpenCV", "Pillow", "Dask", "Ray",
  // Async / task queues
  "Celery", "Dramatiq", "asyncio", "aiohttp",
  // ORM / DB tools
  "SQLAlchemy", "Alembic", "Peewee", "Tortoise ORM",
  // Package / env
  "Poetry", "Conda", "pip", "virtualenv", "Pipenv",
  // ASGI / WSGI servers
  "Uvicorn", "Gunicorn", "Hypercorn",
  // Utilities
  "Pydantic", "Pytest", "Werkzeug",

  // ── ColdFusion & ecosystem ───────────────────────────────────────────────────
  "ColdFusion", "Adobe ColdFusion", "CFML", "ColdFusion Markup Language",
  "Lucee", "Railo", "BoxLang",
  // CF frameworks
  "ColdBox", "FW/1", "Framework One", "CFWheels", "Fusebox", "Mach-II",
  // CF tools
  "CommandBox", "CFIDE", "ColdFusion Builder",

  // ── Shopify & ecosystem ──────────────────────────────────────────────────────
  "Shopify", "Shopify Plus",
  // Templating / language
  "Liquid", "Liquid template",
  // Frameworks / runtimes
  "Hydrogen", "Oxygen", "Remix",
  // APIs
  "Shopify API", "Shopify Admin API", "Shopify Storefront API",
  // Tools / CLI
  "Shopify CLI", "Theme Kit", "Theme Check",
  // Design system / other
  "Polaris", "Shopify Polaris", "Metafields", "Shopify Flow", "Shopify Apps",

  // ── React.js & ecosystem ─────────────────────────────────────────────────────
  "React", "React.js", "JSX", "TSX",
  // Frameworks built on React
  "Next.js", "Gatsby", "Create React App",
  // State management
  "Redux", "Redux Toolkit", "RTK Query", "Zustand", "Jotai", "Recoil", "MobX",
  // Routing
  "React Router", "TanStack Router",
  // Data fetching
  "React Query", "TanStack Query", "SWR", "Apollo Client",
  // Forms & validation
  "Formik", "React Hook Form", "Yup", "Zod",
  // UI libraries
  "Material UI", "MUI", "Ant Design", "Chakra UI", "shadcn/ui", "Shadcn",
  "Radix UI", "Headless UI", "Mantine", "React Bootstrap",
  // Animation
  "Framer Motion", "React Spring",
  // Styling in React
  "Styled Components", "Emotion", "CSS Modules",
  // Testing
  "React Testing Library",
  // Other
  "React Native", "Expo", "Storybook",

  // ── Vue.js & ecosystem ───────────────────────────────────────────────────────
  "Vue", "Vue.js", "Vue 3", "Vue 2",
  // Frameworks
  "Nuxt", "Nuxt.js", "Nuxt 3",
  // State management
  "Pinia", "Vuex",
  // Routing
  "Vue Router",
  // UI libraries
  "Vuetify", "Quasar", "Element Plus", "PrimeVue", "Naive UI", "BootstrapVue",
  // Composition helpers
  "VueUse",
  // Build
  "Vue CLI",

  // ── Node.js & ecosystem ──────────────────────────────────────────────────────
  "Node.js", "Node",
  // Web frameworks
  "Express", "Express.js", "Fastify", "NestJS", "Hapi", "Koa",
  "Sails.js", "AdonisJS", "Feathers.js", "Restify",
  // Real-time
  "Socket.io", "Socket.IO", "ws",
  // ORM / query builders
  "Prisma", "Sequelize", "TypeORM", "Mongoose", "Knex",
  // Package managers / build
  "npm", "Yarn", "pnpm", "Webpack", "Rollup", "esbuild", "Turbopack",
  // Testing
  "Mocha", "Chai", "Sinon", "Supertest",
  // Process management
  "PM2",
  // Auth / utilities
  "Passport.js", "JWT", "bcrypt",

  // ── Other frontend ───────────────────────────────────────────────────────────
  "Angular", "Svelte", "SvelteKit", "Astro", "Solid.js", "Qwik",
  "HTML", "CSS", "Sass", "SCSS", "Tailwind", "Bootstrap",
  "GraphQL", "WebSocket", "Vite", "Jest", "Cypress", "Vitest", "Playwright",

  // ── Backend / other languages ────────────────────────────────────────────────
  "JavaScript", "TypeScript", "Java", "Golang", "Rust", "Ruby", "PHP",
  "Swift", "Kotlin", "Scala", "Perl", "Bash", "Shell", "PowerShell",
  "C++", "C#", "Haskell", "Elixir", "Clojure",
  "Rails", "Laravel", "Spring Boot", "Spring", "ASP.NET", ".NET",
  "Gin", "Fiber", "Actix",

  // ── Databases ────────────────────────────────────────────────────────────────
  "PostgreSQL", "MySQL", "MongoDB", "Redis", "Elasticsearch", "DynamoDB",
  "Cassandra", "Oracle", "SQLite", "Firebase", "Supabase", "CockroachDB",
  "SQL Server", "MariaDB", "SQL", "NoSQL", "Snowflake", "BigQuery",

  // ── AI / ML / Data ───────────────────────────────────────────────────────────
  "Machine Learning", "Deep Learning", "Natural Language Processing",
  "NLP", "Computer Vision", "MLOps", "LLM", "LLMs", "Generative AI", "RAG",
  "Reinforcement Learning", "Data Science", "GPT", "BERT", "Claude",
  "Agent frameworks", "Agentic AI", "AI agents", "Conversational AI", "Prompt engineering",
  "LangChain", "LangGraph", "LlamaIndex", "Llama Index",
  "Vector Database", "Vector DB", "Pinecone", "FAISS", "Chroma", "ChromaDB",
  "Spark", "Hadoop", "Databricks", "Airflow",

  // ── Cloud & Infrastructure ───────────────────────────────────────────────────
  "AWS", "Azure", "GCP", "Google Cloud", "Terraform", "Kubernetes", "Docker",
  "Helm", "OpenShift", "Serverless", "S3", "Lambda", "EC2", "CloudFormation",
  "CloudWatch", "IAM", "VPC",

  // ── DevOps / CI/CD ───────────────────────────────────────────────────────────
  "CI/CD", "Jenkins", "GitHub Actions", "GitLab CI", "CircleCI", "ArgoCD",
  "Ansible", "Nginx", "Apache", "Linux", "Unix", "DevOps", "SRE", "IaC",

  // ── Observability ────────────────────────────────────────────────────────────
  "Prometheus", "Grafana", "Datadog", "Splunk", "New Relic", "OpenTelemetry",

  // ── Messaging ────────────────────────────────────────────────────────────────
  "Kafka", "RabbitMQ", "SQS", "Pub/Sub",

  // ── Tools ────────────────────────────────────────────────────────────────────
  "Git", "GitHub", "GitLab", "Bitbucket", "Jira", "Confluence", "Figma",
  "Postman", "Swagger", "OpenAPI", "Cursor",

  // ── Practices & Architecture ─────────────────────────────────────────────────
  "Microservices", "REST API", "RESTful", "gRPC",
  "Agile", "Scrum", "Kanban", "TDD", "BDD", "DDD", "Event-driven", "CQRS",

  // ── Mobile / Other ───────────────────────────────────────────────────────────
  "Flutter", "iOS", "Android",
  "Blockchain", "Solidity", "Ethereum", "Web3", "WebAssembly",
];

const SOFT_LIST: string[] = [
  "Leadership", "Communication", "Collaboration", "Teamwork",
  "Mentorship", "Mentoring", "Coaching",
  "Problem-solving", "Problem solving", "Critical thinking",
  "Analytical thinking", "Analytical",
  "Time management", "Project management", "Program management",
  "Stakeholder management", "Stakeholder",
  "Cross-functional", "Cross functional",
  "Attention to detail", "Detail-oriented",
  "Self-motivated", "Self-starter",
  "Fast learner", "Quick learner",
  "Adaptable", "Adaptability", "Flexibility",
  "Initiative", "Proactive", "Proactively",
  "Creative", "Creativity", "Innovative", "Innovation",
  "Strategic thinking", "Strategic",
  "Presentation", "Negotiation", "Interpersonal",
  "Customer-facing", "Client-facing", "Customer-centric",
  "Results-driven", "Results-oriented", "Goal-oriented",
  "Accountability", "Ownership", "Empathy",
  "Organizational", "Organizational skills",
  "Written communication", "Verbal communication",
  "Decision-making", "Conflict resolution",
];

// ── Build regex + lookup once at module load ─────────────────────────────────

const _combined: [string, KeywordCategory][] = [
  ...TECH_LIST.map((k): [string, KeywordCategory] => [k, "tech"]),
  ...SOFT_LIST.map((k): [string, KeywordCategory] => [k, "soft"]),
].sort((a, b) => b[0].length - a[0].length); // longest first → greediest match wins

const _map = new Map<string, KeywordCategory>(
  _combined.map(([k, cat]) => [k.toLowerCase(), cat]),
);

const _patterns = _combined.map(([k]) => {
  const escaped = k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Guard both ends — always apply suffix so "C++" won't match inside "C++17"
  const prefix = /^\w/.test(k) ? "(?<![a-zA-Z0-9])" : "";
  const suffix = "(?![a-zA-Z0-9])";
  return `${prefix}${escaped}${suffix}`;
});

const KEYWORD_REGEX = new RegExp(`(${_patterns.join("|")})`, "gi");

export interface TextSegment {
  text: string;
  category: KeywordCategory | null;
}

export function highlightKeywords(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let lastIndex = 0;
  KEYWORD_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = KEYWORD_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index), category: null });
    }
    const category = _map.get(match[0].toLowerCase()) ?? null;
    segments.push({ text: match[0], category });
    lastIndex = KEYWORD_REGEX.lastIndex;
  }

  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), category: null });
  }

  return segments;
}
